import React, { useState, useEffect, useCallback } from 'react';
import { useRefresh } from '../RefreshContext';
import { BACKEND_URL } from '../config';
import OrderNew from './OrderNew';
import { useLanguage } from '../contexts/LanguageContext';

export default function TableInfo({ table, onClose, theme, onEdit, onOrderSelect, user }) {
    const { t } = useLanguage();
    const [showOrderNew, setShowOrderNew] = useState(false);
    const [newTabName, setNewTabName] = useState('');
    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [ordersError, setOrdersError] = useState(null);
    const [parentOrderNames, setParentOrderNames] = useState({});
    const { refreshKey } = useRefresh();

    // Check if user can delete orders (testdev user or owner role)
    const canDeleteOrders = user?.user_name === 'testdev' || user?.role_title?.toLowerCase() === 'owner';

    const fetchOrders = useCallback(() => {
        setOrdersLoading(true);
        setOrdersError(null);
        fetch(`${BACKEND_URL}/api/orders`)
            .then(res => res.json())
            .then(data => {
                const tableOrders = data.filter(order => order.table_id === table.id);
                setOrders(tableOrders);

                const parentNames = {};
                for (const order of tableOrders) {
                    if (order.parent_tab_id) {
                        const parentOrder = data.find(p => p.id === order.parent_tab_id);
                        if (parentOrder && parentOrder.name) {
                            parentNames[order.id] = parentOrder.name.substring(0, 5);
                        }
                    }
                }
                setParentOrderNames(parentNames);
            })
            .catch(err => setOrdersError(t('error') + ': Failed to load orders'))
            .finally(() => setOrdersLoading(false));
    }, [table, t]);

    useEffect(() => {
        if (table && table.id) {
            fetchOrders();
        }
    }, [table, refreshKey, fetchOrders]);

    async function handleDeleteOrder(orderId, orderName) {
        if (!window.confirm(t('deleteOrderConfirmation').replace('this order', `"${orderName}"`))) {
            return;
        }

        try {
            const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                const data = await response.json();
                alert(t('error') + ': ' + (data.error || 'Failed to delete order'));
                return;
            }

            // Success - refresh the orders list
            fetchOrders();
        } catch (err) {
            alert(t('error') + ': ' + (err.message || 'Failed to delete order'));
        }
    }

    if (!table) return null;
    return (
        <>
            <div className="table-info-modal" style={{
                padding: '1rem',
                background: theme?.colors?.surface || '#fff',
                color: theme?.colors?.text || '#222',
                borderRadius: theme?.borderRadius || 8,
                boxShadow: theme?.boxShadow || '0 2px 8px #0002',
                maxWidth: 320,
                border: `2px solid ${theme?.colors?.primary || '#1976d2'}`
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3 style={{ color: theme?.colors?.primary || '#1976d2', marginTop: 0, marginBottom: 0 }}>{t('tableName')} : {table.name}</h3>
                    <button
                        title={t('editTable')}
                        onClick={() => onEdit && onEdit(table)}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            marginLeft: 8,
                            color: theme?.colors?.primary || '#1976d2',
                            padding: 0
                        }}
                    >
                        ✏️
                    </button>
                </div>
                <div>{t('open')} {t('tabs')}:</div>
                {/* <div><strong>Size:</strong> {table.width} x {table.height}</div>
                <div><strong>Position:</strong> ({table.x}, {table.y})</div> */}
                <div style={{ marginTop: 16 }}>
                    {ordersLoading ? (
                        <div>{t('loading')} {t('orders').toLowerCase()}...</div>
                    ) : ordersError ? (
                        <div style={{ color: 'red' }}>{ordersError}</div>
                    ) : orders.length === 0 ? (
                        <div style={{ color: theme?.colors?.textSecondary || '#888' }}>{t('no')} {t('orders').toLowerCase()} {t('forThisTable')}.</div>
                    ) : (
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                            {orders
                                .slice() // copy
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map(order => (
                                    <li key={order.id} style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <button
                                            style={{
                                                flex: 1,
                                                background: theme?.colors?.primary || '#1976d2',
                                                color: theme?.colors?.surface || '#fff',
                                                border: 'none',
                                                borderRadius: theme?.borderRadius || 8,
                                                padding: '0.25rem 0.75rem',
                                                cursor: 'pointer',
                                                fontWeight: 600
                                            }}
                                            onClick={() => onOrderSelect && onOrderSelect(order.id)}
                                        >
                                            {order.name}
                                            {parentOrderNames[order.id] && <span style={{ fontSize: '0.8rem', opacity: 0.8 }}> ({t('from')} {parentOrderNames[order.id]})</span>}
                                        </button>
                                        {canDeleteOrders && (
                                            <button
                                                title={t('deleteOrder')}
                                                style={{
                                                    background: theme?.colors?.error || '#d32f2f',
                                                    color: theme?.colors?.surface || '#fff',
                                                    border: 'none',
                                                    borderRadius: theme?.borderRadius || 8,
                                                    padding: '0.25rem 0.5rem',
                                                    cursor: 'pointer',
                                                    fontSize: '1rem',
                                                    fontWeight: 'bold'
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteOrder(order.id, order.name);
                                                }}
                                            >
                                                🗑️
                                            </button>
                                        )}
                                    </li>
                                ))}
                        </ul>
                    )}
                </div>

                {/* Add Tab Section */}
                <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.9rem', fontWeight: 'bold', color: theme?.colors?.text || '#222' }}>
                        {t('newTabName')}:
                    </label>
                    <input
                        type="text"
                        value={newTabName}
                        onChange={(e) => setNewTabName(e.target.value)}
                        placeholder={t('enterTabName')}
                        style={{
                            padding: '0.5rem',
                            borderRadius: theme?.borderRadius || 4,
                            border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                            backgroundColor: theme?.colors?.surface || '#fff',
                            color: theme?.colors?.text || '#000',
                            fontSize: '1rem'
                        }}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && newTabName.trim()) {
                                setShowOrderNew(true);
                            }
                        }}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            onClick={onClose}
                            style={{
                                flex: 1,
                                background: theme?.colors?.secondary || '#666',
                                color: theme?.colors?.surface || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 8,
                                padding: '0.5rem 1rem',
                                cursor: 'pointer'
                            }}
                        >
                            {t('back')}
                        </button>
                        <button
                            disabled={!newTabName.trim()}
                            style={{
                                flex: 1,
                                background: !newTabName.trim() ? '#ccc' : theme?.colors?.primary || '#1976d2',
                                color: theme?.colors?.surface || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 8,
                                padding: '0.5rem 1rem',
                                cursor: !newTabName.trim() ? 'not-allowed' : 'pointer',
                                opacity: !newTabName.trim() ? 0.6 : 1
                            }}
                            onClick={() => setShowOrderNew(true)}
                        >
                            {t('addTab')}
                        </button>
                    </div>
                </div>
                {/* <button
                    style={{
                        marginTop: 12,
                        background: orders.some(order => order.closed === 0) ? theme?.colors?.secondary || '#aaa' : theme?.colors?.error || '#d32f2f',
                        color: theme?.colors?.surface || '#fff',
                        border: 'none',
                        borderRadius: theme?.borderRadius || 8,
                        padding: '0.5rem 1rem',
                        cursor: orders.some(order => order.closed === 0) ? 'not-allowed' : 'pointer',
                        marginRight: 8,
                        opacity: orders.some(order => order.closed === 0) ? 0.6 : 1
                    }}
                    disabled={orders.some(order => order.closed === 0)}
                    onClick={async () => {
                        if (orders.some(order => order.closed === 0)) return;
                        if (!window.confirm('Are you sure you want to delete this table?')) return;
                        try {
                            const res = await fetch(`${BACKEND_URL}/api/tables/${table.id}`, {
                                method: 'DELETE',
                            });
                            if (!res.ok) throw new Error('Failed to delete table');
                            if (onClose) onClose();
                        } catch (err) {
                            alert('Failed to delete table: ' + err.message);
                        }
                    }}
                >
                    Delete Table
                </button> */}
            </div>
            {showOrderNew && (
                <OrderNew
                    tableId={table.id}
                    theme={theme}
                    user={user}
                    initialOrderName={newTabName}
                    onClose={() => {
                        setShowOrderNew(false);
                        setNewTabName('');
                    }}
                    onOrderCreated={data => {
                        setShowOrderNew(false);
                        setNewTabName('');
                        fetchOrders();
                        if (data && data.id && onOrderSelect) onOrderSelect(data.id);
                    }}
                />
            )}
        </>
    );
}
